import type JhaButton from '@banno/jha-wc/src/buttons/jha-button/jha-button';
import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { delay, exists } from '@jack-henry/frontend-utils/functions';
import { DynamicComponentExitReason } from '@jack-henry/frontend-utils/lit/dynamic-component-registry';
import { ObservationSource } from '@jack-henry/frontend-utils/observable';
import { type Action } from '@jack-henry/frontend-utils/types';
import type JhPlatformDrawer from '@jack-henry/platform-tools/components/jh-platform-drawer';
import { WindowService } from '@treasury/utils';
import { waitForAnimations } from '@treasury/utils/functions';
import { css, html, LitElement, PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { createRef, ref } from 'lit/directives/ref.js';
import {
    getContentFromSlot,
    ISideDrawerContent,
    isSideDrawerContent,
    listenForDrawerResult,
    SideDrawerButton,
    SideDrawerButtonPosture,
} from './side-drawer.service.types';

// eslint-disable-next-line no-duplicate-imports
import '@banno/jha-wc/src/buttons/jha-button/jha-button';
// eslint-disable-next-line no-duplicate-imports
import '@banno/jha-wc/src/buttons/jha-button/jha-button';
// eslint-disable-next-line no-duplicate-imports
import '@jack-henry/platform-tools/components/jh-platform-drawer';

export const tagName = 'tm-side-drawer';

@customElement(tagName)
export class TmSideDrawer extends LitElement {
    @property()
    public heading = '';

    @property({
        type: String,
        reflect: true,
    })
    public readonly alignment: 'right' | 'left' = 'right';

    @property({
        type: String,
        reflect: true,
    })
    public readonly footerAlignment: 'right' | 'left' = 'left';

    @property({
        type: Boolean,
    })
    public readonly closeOnBlur = true;

    @property({
        attribute: false,
        reflect: false,
    })
    public buttons: SideDrawerButton[] = [];

    public get closeComplete() {
        if (!this.drawer) {
            throw new Error('Could not determine closed state');
        }

        return this.waitForAnimations();
    }

    /**
     * A promise that resolves when the drawer is ready for interaction.
     *
     * Exists mostly to support testing scenarios.
     */
    public get readyForInteraction() {
        return this.interactivitySource.toPromise(false);
    }

    @InjectProperty()
    declare private readonly window: WindowService;

    @state()
    private closed = false;

    @state()
    private data?: unknown;

    /**
     * Indicates whether the drawer is ready to interact with user input.
     *
     * Used to determine if clicks outside the drawer should close it.
     */
    private isInteractive = false;

    private readonly interactivitySource = new ObservationSource<void>();

    private drawer?: JhPlatformDrawer;

    private readonly slotRef = createRef<HTMLSlotElement>();

    private readonly buttonContainerRef = createRef<Element>();

    private readonly disconnectedTasks: Action[] = [];

    private drawerHandle?: ReturnType<typeof listenForDrawerResult>;

    public firstUpdated(changedProps: PropertyValues): void {
        super.firstUpdated(changedProps);
        this.listenForBlur();
    }

    public disconnectedCallback() {
        this.disconnectedTasks.forEach(task => task());
        this.drawerHandle?.unsubscribe();
        this.drawerHandle = undefined;
    }

    /**
     * Close the drawer programmatically from an external source.
     */
    public close<T>(reason = DynamicComponentExitReason.ForceClose, data?: T) {
        this.closed = true;
        data = data || (this.data as T);

        const result = {
            data,
            reason,
        };

        const closeEvent = new CustomEvent('close-drawer', {
            detail: result,
        });
        this.dispatchEvent(closeEvent);
    }

    public getButtons() {
        const { value: buttonContainer } = this.buttonContainerRef;
        if (!buttonContainer) {
            throw new Error('Could not get side drawer buttons');
        }

        return Array.from(buttonContainer.querySelectorAll<JhaButton>('jha-button'));
    }

    public render() {
        const conditionalClasses = classMap({
            [`slide-in-${this.alignment}`]: !this.closed,
            [`slide-out-${this.alignment}`]: this.closed,
        });

        const overlayClasses = classMap({
            overlay: true,
            'fade-in': !this.closed,
            'fade-out': this.closed,
        });

        const overlay = !this.closeOnBlur ? html`<div class="${overlayClasses}"></div>` : '';

        return html`${overlay}<jh-platform-drawer
                ${ref(drawer => {
                    this.drawer = drawer as JhPlatformDrawer;
                    this.listenForResult();
                })}
                class="${conditionalClasses} max-w-full"
                .heading=${this.heading}
                @close-drawer=${() => {
                    this.closed = true;
                }}
            >
                <slot ${ref(this.slotRef)} @slotchange=${() => this.onSlotChange()}></slot>
                <div
                    slot="footer"
                    id="footer"
                    class=${classMap({
                        'flex flex-row': true,
                        'justify-end': this.footerAlignment === 'right',
                        'justify-start': this.footerAlignment === 'left',
                    })}
                >
                    ${this.renderButtons()}
                </div>
            </jh-platform-drawer>`;
    }

    private listenForResult() {
        if (!this.drawer) {
            return;
        }

        if (this.drawerHandle) {
            this.drawerHandle.unsubscribe();
            this.drawerHandle = undefined;
        }

        this.drawerHandle = listenForDrawerResult(this.drawer, result => {
            this.data = result;
        });
    }

    private onSlotChange() {
        const { value: slot } = this.slotRef;

        if (!slot) {
            return;
        }

        const content = getContentFromSlot(slot);
        if (!exists(content)) {
            return;
        }

        if (isSideDrawerContent(content)) {
            this.hydrateFromContentProvider(content);

            // keep drawer chrome in sync with its content
            // in case label state depends on content state
            const { unsubscribe } = content.updated$.subscribe(() => this.requestUpdate());

            this.disconnectedTasks.push(unsubscribe);
        }

        // introduce a slight delay so that the drawer does not immediately
        // closes when the UI element to open it is clicked
        this.waitForAnimations()
            .then(() => delay(100))
            .then(() => {
                this.isInteractive = true;
                this.interactivitySource.emit();
            });
    }

    private hydrateFromContentProvider(content: ISideDrawerContent) {
        const { drawerButtons = [] } = content;
        const merged = [...this.buttons, ...drawerButtons];

        this.buttons = merged;
    }

    private renderButtons() {
        const buttonTemplates = this.buttons.map(b => {
            const { onClick, checkEnabled, reason, label: labelOrFunc } = b;
            const isEnabled = checkEnabled ? checkEnabled(this.data) : true;
            const buttonRef = createRef<JhaButton>();
            // if no click handler is provided, buttons will close the drawer by default
            const clickHandler = async () => {
                try {
                    if (!onClick) {
                        this.close(reason);
                    } else {
                        await onClick(
                            data => this.close(reason, data),
                            data => (this.data = data)
                        );
                    }
                } finally {
                    if (buttonRef.value) {
                        // if the button is sync, it will be in a loading state
                        buttonRef.value.isLoading = false;
                    }
                }
            };
            const label = typeof labelOrFunc === 'string' ? labelOrFunc : labelOrFunc();

            return html`<jha-button
                ${ref(buttonRef)}
                ?primary=${b.posture === SideDrawerButtonPosture.Primary}
                ?outline-primary=${b.posture === SideDrawerButtonPosture.Secondary}
                ?tertiary=${b.posture === SideDrawerButtonPosture.Tertiary}
                ?success=${b.posture === SideDrawerButtonPosture.Constructive}
                ?danger=${b.posture === SideDrawerButtonPosture.Destructive}
                class="drawer-button"
                ?disabled=${!isEnabled}
                @click=${() => clickHandler()}
                >${label}</jha-button
            >`;
        });

        // place button templates in a container div so that they can be queried easily
        return html`<div ${ref(this.buttonContainerRef)}>${buttonTemplates}</div>`;
    }

    private listenForBlur() {
        if (!this.closeOnBlur) {
            return;
        }

        const { unsubscribe } = this.window.addEventListener('click', e => this.handleBlur(e));
        this.disconnectedTasks.push(unsubscribe);
    }

    /**
     * Interrogates all click events on the window to determine if the user clicked outside
     * of the drawer when `closeOnBlur` is enabled.
     *
     * @param e Click event enacted anywhere on the window.
     */
    private handleBlur(e: Event) {
        if (!this.drawer || !this.isInteractive) {
            return;
        }

        const eventPath = e.composedPath();
        if (eventPath.includes(this)) {
            return;
        }

        this.close(DynamicComponentExitReason.Cancel);
    }

    private async waitForAnimations() {
        if (!this.drawer) {
            return;
        }

        await waitForAnimations(this.drawer);
    }

    public static get styles() {
        return css`
            :host {
                display: flex;
                height: 100%;
                max-width: 100%;
                --jh-platform-drawer-width: auto;
            }
            jh-platform-drawer {
                --jh-color-container-page: var(--container-primary, #e0e0e0);
                --jh-color-divider-primary: var(--divider-primary, #e0e0e0);
                --jh-color-content-primary-enabled: var(--primary-text-color, #000000);
                color: var(--primary-text-color, #000000);
            }
            #footer {
                display: flex;
                flex-direction: row;
                justify-content: flex-end;
            }
            .drawer-button {
                padding: 4px;
            }
            .slide-in-right {
                animation: slide-in-right 0.5s;
            }

            .slide-out-right {
                animation: slide-out-right 1s forwards;
            }

            .slide-in-left {
                animation: slide-in-left 0.5s;
            }

            .slide-out-left {
                animation: slide-out-left 1s forwards;
            }

            @keyframes slide-in-left {
                from {
                    translate: -150vw 0;
                    display: none;
                }

                to {
                    translate: 0 0;
                }
            }

            @keyframes slide-out-left {
                from {
                    translate: 0 0;
                }

                to {
                    translate: -150vw 0;
                    display: none;
                }
            }

            @keyframes slide-in-right {
                from {
                    translate: 150vw 0;
                    display: none;
                }

                to {
                    translate: 0 0;
                }
            }

            @keyframes slide-out-right {
                from {
                    translate: 0 0;
                }

                to {
                    translate: 150vw 0;
                    display: none;
                }
            }

            .overlay {
                position: fixed;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                background-color: rgba(0, 0, 0, 0.5);
                z-index: -1;
            }

            .fade-in {
                animation: fade-in 0.3s ease-in;
            }

            .fade-out {
                animation: fade-out 0.3s ease-out forwards;
            }

            @keyframes fade-in {
                from {
                    opacity: 0;
                }
                to {
                    opacity: 1;
                }
            }

            @keyframes fade-out {
                from {
                    opacity: 1;
                }
                to {
                    opacity: 0;
                }
            }
        `;
    }
}
